// renderOnePiece.js


export function renderOnePiece(piece, puzzleImageElement, options = {}) {

  const scale = 1; // Пока рендерим только 1:1
  const { 
    tabSizePercent = 0.2, 
    shadow, 
    stroke = { color: '#33333388', width: Math.min(3, piece.baseDimensions.width * 0.015) }, 
    forceRedraw,
   } = options;


  // Кеш используется только если нет эффектов
  if (piece.cached && !forceRedraw) { return piece.cached; }


  if (!piece || !piece.path || !piece.baseDimensions || !puzzleImageElement || !puzzleImageElement.complete) {
    throw new Error('Invalid input: piece or puzzleImageElement is missing or incomplete');
  }

  const baseWidth = piece.baseDimensions.width;
  const baseHeight = piece.baseDimensions.height;

  const maxDimension = Math.max(baseWidth, baseHeight);
  const maxExtension = maxDimension * tabSizePercent;
  const maxTotal = maxDimension + maxExtension * 2;

  const tabExtensionX = (maxTotal - baseWidth) / 2;
  const tabExtensionY = (maxTotal - baseHeight) / 2;
  const totalWidth = maxTotal;
  const totalHeight = maxTotal;


  const canvasWidth = totalWidth;
  const canvasHeight = totalHeight;

  // Смещение для центрирования фигуры с учетом тени
  const translateX = tabExtensionX;
  const translateY = tabExtensionY;

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth * scale; // scale пока всегда 1
  canvas.height = canvasHeight * scale;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get 2D context for offscreen canvas');
  }

  ctx.save(); // 1. Сохраняем исходное состояние

  // Сдвигаем систему координат для центрирования и учета тени
  ctx.translate(translateX, translateY);
  if (shadow) {
    renderShadow(ctx, piece, shadow);
  }

  ctx.save(); // 2. Сохраняем перед клиппингом
  drawPiecePath(ctx, piece); // Определяем путь для клиппинга
  ctx.clip();

  // Расчеты для drawImage
  const imgWidth = puzzleImageElement.width;
  const imgHeight = puzzleImageElement.height;
  const imgTabWidth = piece.imageCrop.tabs.width;
  const imgTabHeight = piece.imageCrop.tabs.height;

  const sourceX = piece.imageCrop.x - imgTabWidth;
  const sourceY = piece.imageCrop.y - imgTabHeight;
  const sourceWidth = piece.imageCrop.width + 2 * imgTabWidth;
  const sourceHeight = piece.imageCrop.height + 2 * imgTabHeight;

  const adjustedSourceX = Math.max(0, sourceX);
  const adjustedSourceY = Math.max(0, sourceY);
  const adjustedSourceWidth = Math.min(imgWidth - adjustedSourceX, sourceWidth + (sourceX - adjustedSourceX));
  const adjustedSourceHeight = Math.min(imgHeight - adjustedSourceY, sourceHeight + (sourceY - adjustedSourceY));

  const destTabX = tabExtensionX;
  const destTabY = tabExtensionY;
  const destX = -destTabX + (adjustedSourceX - sourceX);
  const destY = -destTabY + (adjustedSourceY - sourceY);
  const destWidth = adjustedSourceWidth;
  const destHeight = adjustedSourceHeight;

  if (adjustedSourceWidth > 0 && adjustedSourceHeight > 0 && destWidth > 0 && destHeight > 0) {
    ctx.drawImage(
      puzzleImageElement,
      adjustedSourceX, adjustedSourceY, adjustedSourceWidth, adjustedSourceHeight,
      destX, destY, destWidth, destHeight
    );
  } else {
    console.warn("Skipping drawImage for piece due to zero dimensions", { pieceId: piece.id });
  }

  // --- Отрисовка Обводки (если нужна) ---
  // Обводка рисуется внутри clip-path поверх изображения
  if (stroke) {
    // Путь уже задан для clip, используем его для stroke
    renderStroke(ctx, piece, stroke);
  }

  ctx.restore(); // 1. Восстанавливаем исходное состояние (убираем translate/scale)

  // Не кешируем результат, если применялись эффекты
  if (!forceRedraw) {
    piece.cached = canvas;
  }

  return canvas;
}


/**
 * Хелпер-функция для отрисовки пути фигуры на контексте.
 * @param {CanvasRenderingContext2D} ctx - Контекст канваса.
 * @param {Object} piece - Объект пазла с полем path.
 */
function drawPiecePath(ctx, piece) {
  ctx.beginPath();
  for (const cmd of piece.path) {
    switch (cmd.cmd) {
      case 'moveTo':
        ctx.moveTo(cmd.x, cmd.y);
        break;
      case 'lineTo':
        ctx.lineTo(cmd.x, cmd.y);
        break;
      case 'bezierCurveTo':
        ctx.bezierCurveTo(cmd.cp1x, cmd.cp1y, cmd.cp2x, cmd.cp2y, cmd.x, cmd.y);
        break;
      case 'closePath':
        ctx.closePath();
        break;
      default:
        console.warn(`Unknown path command: ${cmd.cmd}`);
    }
  }
}

/**
 * Хелпер-функция для отрисовки тени.
 * @param {CanvasRenderingContext2D} ctx - Контекст канваса.
 * @param {Object} piece - Объект пазла.
 * @param {Object} shadowOptions - Параметры тени.
 */
function renderShadow(ctx, piece, shadowOptions) {
  ctx.save();
  ctx.shadowColor = shadowOptions.color || 'rgba(0, 0, 0, 0.5)';
  ctx.shadowBlur = shadowOptions.blur || 5;
  ctx.shadowOffsetX = shadowOptions.offsetX || 0;
  ctx.shadowOffsetY = shadowOptions.offsetY || 0;

  drawPiecePath(ctx, piece);
  // Цвет заливки не важен, т.к. он нужен только для создания тени
  ctx.fillStyle = 'rgba(0,0,0,1)';
  ctx.fill();

  ctx.restore(); // Убираем настройки тени
}

/**
 * Хелпер-функция для отрисовки обводки.
 * @param {CanvasRenderingContext2D} ctx - Контекст канваса.
 * @param {Object} piece - Объект пазла.
 * @param {Object} strokeOptions - Параметры обводки.
 */
function renderStroke(ctx, piece, strokeOptions) {
  // Нормализуем strokeOptions до массива, если это один объект
  const definitions = Array.isArray(strokeOptions) ? strokeOptions : [strokeOptions];

  if (!definitions || definitions.length === 0 || !definitions[0]?.width) {
    // Если нет параметров или ширины, не рисуем обводку
    return;
  }

  ctx.save(); // Сохраняем состояние контекста перед отрисовкой обводок

  // Определяем путь один раз для всех обводок
  drawPiecePath(ctx, piece);

  // Итерируем по каждому определению стиля обводки
  for (const def of definitions) {
    if (!def || !def.width) continue; // Пропускаем некорректные определения

    ctx.strokeStyle = def.color || '#000';
    ctx.lineWidth = def.width;
    ctx.setLineDash(def.dash || []); // Устанавливаем пунктир (пустой массив = сплошная линия)
    ctx.lineDashOffset = def.offset || 0; // Устанавливаем смещение пунктира

    ctx.stroke(); // Рисуем обводку с текущими настройками
  }

  // Сбрасываем настройки пунктира и восстанавливаем контекст
  ctx.setLineDash([]);
  ctx.lineDashOffset = 0;
  ctx.restore();
}

